import {
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Spin,
  Switch,
  Typography,
} from "antd";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { ICreateEmployee, IQuery } from "../../../models/Hrm";
import {
  useCreateEmployee,
  useGetUsersWithoutEmployee,
} from "../../../query/HrmQuery";
import { notify } from "../../../utils/toast";
import { AxiosError } from "axios";

const { Text } = Typography;

interface ModalCreateEmployeeProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const ModalCreateEmployee = ({ open, setOpen }: ModalCreateEmployeeProps) => {
  const [form] = Form.useForm();
  const [filter, setFilter] = useState<IQuery>({
    page: 1,
    limit: 10,
  });
  const { data, isFetching } = useGetUsersWithoutEmployee(filter);
  const [allUsers, setAllUsers] = useState<any[]>([]);

  useEffect(() => {
    if (data?.data) {
      if (filter.page === 1) {
        setAllUsers(data.data);
      } else {
        setAllUsers((prev) => [
          ...prev,
          ...data.data.filter((u: any) => !prev.some((p) => p.id === u.id)),
        ]);
      }
    }
  }, [data, filter.page]);

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setFilter((prev) => ({
          ...prev,
          page: 1,
          keyword: value.trim() || undefined,
        }));
      }, 300),
    [],
  );

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [open, form]);

  const createEmployeeMutation = useCreateEmployee();

  const handleCreateEmployee = async (values: any) => {
    try {
      if (Number(values.employeeNo) < 0) {
        message.warning("Vui lòng để mã nhân viên lớn hơn 0");
        return;
      }

      const payload: Partial<ICreateEmployee> = {
        userId: values.userId,
        hiredAt: values.hiredAt?.format("YYYY-MM-DD"),
        born: values.born?.format("YYYY-MM-DD"),
        employeeNo: values.employeeNo,
        leaveBalance: values.leaveBalance,
        gender: values.gender,
        isOvertime: values.isOvertime,
        phone: values.phone,
        addressPermanent: values.addressPermanent,
        addressCurrent: values.addressCurrent,
      };

      await createEmployeeMutation.mutateAsync({
        data: payload,
        query: filter,
      });
      notify.success("Tạo nhân viên thành công");
      setOpen(false);
      form.resetFields();
    } catch (error: any) {
      const msg = error as AxiosError<{ message: string }>;
      notify.error(msg.response?.data?.message ?? "Thao tác thất bại");
    }
  };

  const selectBefore = (
    <Text strong className="text-gray-600">
      +84
    </Text>
  );
  return (
    <Modal
      title="Tạo nhân viên"
      open={open}
      onCancel={() => setOpen(false)}
      onOk={() => form.submit()}
      okText="Tạo nhân viên dùng cho máy chấm công"
      cancelText="Hủy"
      okButtonProps={{
        loading: createEmployeeMutation.isPending,
      }}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleCreateEmployee}
        initialValues={{ isOvertime: false }}
      >
        <Flex vertical gap="middle">
          <Form.Item
            name="userId"
            rules={[{ required: true, message: "Vui lòng chọn người dùng" }]}
            style={{ marginBottom: 0 }}
          >
            <Select
              showSearch
              className="w-full"
              placeholder="Chọn người dùng"
              filterOption={false}
              onSearch={(value) => debouncedSearch(value)}
              listHeight={100}
              options={allUsers.map((emp) => ({
                value: emp.id,
                label: `${emp.fullName} - ${emp.email || "Chưa có email"}`,
              }))}
              onPopupScroll={(e) => {
                const target = e.target as HTMLDivElement;
                if (
                  !isFetching &&
                  Math.round(target.scrollTop + target.offsetHeight) >=
                    target.scrollHeight - 10
                ) {
                  if (
                    data?.pagination &&
                    (filter.page || 1) * (filter.limit || 10) <
                      data.pagination.total
                  ) {
                    setFilter((prev) => ({
                      ...prev,
                      page: prev.page! + 1,
                    }));
                  }
                }
              }}
              notFoundContent={
                isFetching ? <Spin size="small" /> : "Không có dữ liệu"
              }
              dropdownRender={(menu) => (
                <>
                  {menu}
                  {isFetching && allUsers.length > 0 && (
                    <Flex justify="center" align="center" className="py-2">
                      <Spin size="small" />
                    </Flex>
                  )}
                </>
              )}
            />
          </Form.Item>

          <Form.Item
            name="hiredAt"
            rules={[
              { required: true, message: "Vui lòng chọn ngày tuyển dụng" },
            ]}
            style={{ marginBottom: 0 }}
          >
            <DatePicker
              placeholder="Ngày tuyển dụng"
              className="w-full"
              format="DD/MM/YYYY"
            />
          </Form.Item>

          <Form.Item
            name="employeeNo"
            rules={[{ required: true, message: "Vui lòng nhập mã nhân viên" }]}
            style={{ marginBottom: 0 }}
          >
            <Input placeholder="Định danh chấm công" />
          </Form.Item>

          <Form.Item
            name="leaveBalance"
            rules={[
              { required: true, message: "Vui lòng nhập số ngày nghỉ phép" },
              {
                validator: (_, value) => {
                  if (value != null && value > 12) {
                    return Promise.reject(
                      new Error("Số ngày nghỉ không được lớn hơn 12"),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              placeholder="Số ngày nghỉ phép (tối đa 12 ngày)"
              max={12}
              min={0}
              className="w-full"
              maxLength={2}
            />
          </Form.Item>

          <Form.Item
            name="gender"
            rules={[{ required: true, message: "Vui lòng chọn giới tính" }]}
            style={{ marginBottom: 0 }}
          >
            <Select
              placeholder="Giới tính"
              className="w-full"
              options={[
                { value: "MALE", label: "Nam" },
                { value: "FEMALE", label: "Nữ" },
                { value: "OTHER", label: "Khác" },
              ]}
            />
          </Form.Item>

          <Flex align="center" justify="space-between" className="w-full">
            <Text>Có tăng ca: </Text>
            <Form.Item
              name="isOvertime"
              valuePropName="checked"
              style={{ marginBottom: 0 }}
            >
              <Switch />
            </Form.Item>
          </Flex>

          <Form.Item
            name="born"
            rules={[{ required: true, message: "Vui lòng chọn ngày sinh" }]}
            style={{ marginBottom: 0 }}
          >
            <DatePicker
              placeholder="Ngày sinh"
              className="w-full"
              format="DD/MM/YYYY"
            />
          </Form.Item>

          <Form.Item
            name="phone"
            rules={[
              { required: true, message: "Vui lòng nhập số điện thoại" },
              {
                pattern: /^[0-9]{10,11}$/,
                message: "Số điện thoại không hợp lệ",
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <Input
              addonBefore={selectBefore}
              placeholder="Số điện thoại"
              className="w-full"
              maxLength={11}
              onKeyDown={(e) => {
                const allowedKeys = [
                  "Backspace",
                  "Delete",
                  "ArrowLeft",
                  "ArrowRight",
                  "Tab",
                ];
                if (allowedKeys.includes(e.key)) return;
                if (!/^\d$/.test(e.key)) e.preventDefault();
              }}
            />
          </Form.Item>

          <Form.Item
            name="addressPermanent"
            rules={[
              { required: true, message: "Vui lòng nhập địa chỉ thường trú" },
            ]}
            style={{ marginBottom: 0 }}
          >
            <Input.TextArea
              placeholder="Địa chỉ thường trú"
              autoSize={{ minRows: 2 }}
            />
          </Form.Item>

          <Form.Item
            name="addressCurrent"
            rules={[
              { required: true, message: "Vui lòng nhập địa chỉ hiện tại" },
            ]}
            style={{ marginBottom: 0 }}
          >
            <Input.TextArea
              placeholder="Địa chỉ hiện tại"
              autoSize={{ minRows: 2 }}
            />
          </Form.Item>
        </Flex>
      </Form>
    </Modal>
  );
};

export default ModalCreateEmployee;
